import * as React from "react";

import { useCommonArrowNavGroup } from "./useCommonArrowNavGroup";
import { useDisableTabTabster } from "./useDisableTabTabster";
import { useFirstFocusableRef } from "./useFirstFocusableHook";

export type TabsterDivProps = {
  /**
   * Children elements within the div
   */
  children?: React.ReactNode;
  /**
   * Custom styles
   */
  className?: string;
  /**
   * Accessible name
   */
  ariaLabel?: string;
  /**
   * Callback ref to control focus on the tablist
   */
  firstFocusableRef?: React.Ref<{ focus: () => void }>;
  /**
   * If true, will add an arrow navigation group from tabster
   */
  useArrowNavGroup?: boolean;

  /**
   * Callback for the keydown event
   */
  onKeyDown?: React.KeyboardEventHandler<HTMLElement>;

  /**
   * Option to focus the selected element instead of first
   */
  focusSelected?: boolean;

  /**
   * Option to let tabster control tab keydown events rather
   * than overwriting them
   */
  useTabsterTabControl?: boolean;
};

export const DivWithTabsterAttributes = React.forwardRef(
  (props: TabsterDivProps, ref: React.ForwardedRef<HTMLDivElement>) => {
    const {
      useArrowNavGroup,
      className,
      ariaLabel,
      children,
      firstFocusableRef,
      onKeyDown,
      focusSelected,
      useTabsterTabControl,
    } = props;

    const divRef = React.useRef<HTMLDivElement>(null);
    // Hook to ensure we focus the correct tab when we
    // call focus on the tablist container
    useFirstFocusableRef({ firstFocusableRef, focusSelected }, divRef);

    // Hook to allow focus to move using arrow keys
    const attributes = useCommonArrowNavGroup(
      useTabsterTabControl ? false : true
    );
    const navGroupProperties = useArrowNavGroup ? attributes : {};

    // Hook to disable tab so we can manually move tab focus
    const tabsterDisabler = useDisableTabTabster();
    const disable = useTabsterTabControl ? {} : tabsterDisabler;

    return (
      <div
        role={"toolbar"}
        data-is-focusable={"true"}
        ref={divRef ? divRef : ref}
        className={className}
        tabIndex={useTabsterTabControl ? -1 : 0}
        aria-label={ariaLabel}
        {...disable}
        {...navGroupProperties}
        onKeyDown={onKeyDown}
      >
        {children}
      </div>
    );
  }
);
